// Parse a "chord + lyrics" textarea into a timeline of {chord, beats} entries.
// Heuristic:
//   1. Identify "chord lines" (lines where >=50% of tokens are recognised chords).
//   2. For each chord line: measure horizontal position (char column) of each chord.
//   3. Convert horizontal spans into beats proportionally, one line = one measure.
//   4. Round each chord's beats so they sum exactly to `beatsPerMeasure`.
import { parseChord } from '@/lib/pianoChords';

const CHORD_TOKEN = /\S+/g;

function isChordToken(tok) {
  // Strip trailing punctuation
  const cleaned = tok.replace(/[.,;:!?]+$/, '');
  const p = parseChord(cleaned);
  return p !== null;
}

function detectChordsInLine(line) {
  const chords = [];
  for (const m of line.matchAll(CHORD_TOKEN)) {
    const tok = m[0].replace(/[.,;:!?]+$/, '');
    if (isChordToken(tok)) {
      chords.push({ chord: tok, col: m.index });
    }
  }
  return chords;
}

function isChordLine(line) {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false;
  const chordTokens = tokens.filter(isChordToken);
  // Require at least 1 chord AND >= 60% of tokens are chords (avoids matching lyric lines
  // where a word happens to be a valid pitch like "A" or "D").
  return chordTokens.length >= 1 && chordTokens.length / tokens.length >= 0.6;
}

/**
 * Distribute a total number of `beats` amongst weighted `slots` such that each
 * slot receives an integer count >= 1 and the sum equals `beats`. Uses the
 * largest-remainder method.
 */
function distributeBeats(weights, beats) {
  const n = weights.length;
  if (n === 0) return [];
  const total = weights.reduce((s, w) => s + w, 0) || n;
  const raw = weights.map(w => (w / total) * beats);
  // Floor and track fractional remainders
  const floor = raw.map(v => Math.max(1, Math.floor(v))); // each slot gets >= 1
  let sum = floor.reduce((s, v) => s + v, 0);
  const remainders = raw.map((v, i) => ({ i, rem: v - Math.floor(v) }));
  // If sum > beats (because of the min-1), remove from smallest remainders (but never go below 1)
  while (sum > beats) {
    // Find slot with beats > 1 and smallest remainder
    const candidates = remainders
      .filter(r => floor[r.i] > 1)
      .sort((a, b) => a.rem - b.rem);
    if (candidates.length === 0) break;
    floor[candidates[0].i] -= 1;
    sum -= 1;
  }
  // If sum < beats, add to largest remainders
  while (sum < beats) {
    remainders.sort((a, b) => b.rem - a.rem);
    floor[remainders[0].i] += 1;
    remainders[0].rem -= 1;
    sum += 1;
  }
  return floor;
}

/**
 * Parse a "chords + lyrics" body into a chord_timeline.
 * Returns { timeline: [{chord, beats}], measures_created: number, warnings: [] }.
 */
export function parseChordText(text, beatsPerMeasure = 4) {
  if (!text || !text.trim()) return { timeline: [], measures: 0, warnings: ['Le champ Accords est vide.'] };
  const lines = text.split('\n');
  const timeline = [];
  const warnings = [];
  let measures = 0;

  for (const line of lines) {
    if (!isChordLine(line)) continue;
    const chords = detectChordsInLine(line);
    if (chords.length === 0) continue;

    // Compute weight per chord = distance to next chord (or line end)
    const lineEnd = Math.max(line.length, chords[chords.length - 1].col + 4);
    const weights = chords.map((c, i) => {
      const next = i < chords.length - 1 ? chords[i + 1].col : lineEnd;
      return Math.max(1, next - c.col);
    });
    const beatsPerChord = distributeBeats(weights, beatsPerMeasure);
    chords.forEach((c, i) => {
      timeline.push({ chord: c.chord, beats: beatsPerChord[i] });
    });
    measures += 1;
  }

  if (measures === 0) {
    warnings.push("Aucune ligne d'accords détectée. Assure-toi que tes accords sont sur une ligne dédiée (ex: 'C   G   Am   F').");
  }

  return { timeline, measures, warnings };
}
